import { Modal as AntModal, Button, Col, Input, Row, Spin } from 'antd'
import { useEffect, useState } from 'react'
import { useApi } from 'admin/lib/Api'
import { DisplayProjectUpdateErrorModal } from './ProjectUpdateErrorModal'

type ModalProps = {
  onSuccess: () => void
  onCancel: () => void
  project: {
    projectId: string
    name: string
    isClosedOut: boolean
  }
  accountId: string
}

type ModalState = 'Confirmation' | 'ClosingOut' | 'Failure'

export function CloseOutModal({
  onSuccess,
  onCancel,
  project,
  accountId
}: ModalProps) {
  const [confirmationTyped, setConfirmationTyped] = useState<string>('')
  const [modalState, setModalState] = useState<ModalState>('Confirmation')

  const api = useApi()

  useEffect(() => {
    if (modalState != 'ClosingOut') return

    async function closeOut() {
      if (!api) return

      const response = await api.closeOutProject(accountId, project.projectId)
      const [, status, error] = response

      if (error) {
        DisplayProjectUpdateErrorModal(error, status)
        setModalState('Failure')
      } else {
        onSuccess()
      }
    }

    closeOut()
  }, [accountId, api, modalState, onSuccess, project.projectId])

  return (
    <AntModal
      title="Close Out Project"
      open={true}
      onCancel={onCancel}
      maskClosable={false}
      closable={modalState != 'ClosingOut'}
      confirmLoading={false}
      footer={null}
      width={600}
      wrapClassName="address-modal"
    >
      <h1>Close Out: {project.name}</h1>

      {modalState == 'Confirmation' && (
        <>
          <p>
            Closing Out a project will remove Mobile App passwords and Email
            sync, therefore stopping incoming tickets (except via Public API).
          </p>

          {project.isClosedOut && (
            <p className="warning">
              This project is already Closed Out. You can do it again and it
              will remove any Mobile App passwords and Email sync that have been
              created since.
            </p>
          )}

          <p className="warning">
            Are you sure you want to Close Out this project? This action cannot
            be undone.
          </p>

          <Row align="middle" gutter={3} style={{ marginBottom: '10px' }}>
            <Col>
              <p className="compress">
                <label className="input-label" htmlFor="confirmationInput">
                  Type "Close" to confirm you want to do this
                </label>
              </p>
            </Col>
            <Col>
              <Input
                id="confirmationInput"
                maxLength={20}
                size="small"
                style={{ width: '150px' }}
                onChange={e => setConfirmationTyped(e.currentTarget.value)}
              />
            </Col>
          </Row>
        </>
      )}

      {modalState == 'ClosingOut' && (
        <>
          <Row>
            <p>Please wait while the Project is Closed Out...</p>
          </Row>
          <Row>
            <Col offset={10} span={4}>
              <Spin />
            </Col>
          </Row>
        </>
      )}

      {modalState == 'Failure' && (
        <Row>
          <p className="error" style={{ width: '100%' }}>
            Failed to Close Out project.
          </p>
        </Row>
      )}

      <ModalButtonRow>
        <ModalButton
          label="Cancel"
          onClick={onCancel}
          disabled={modalState == 'ClosingOut'}
        />
        <ModalButton
          label="Close Out Project"
          onClick={() => setModalState('ClosingOut')}
          primary
          disabled={
            modalState != 'Confirmation' || confirmationTyped != 'Close'
          }
        />
      </ModalButtonRow>
    </AntModal>
  )
}

function ModalButtonRow({ children }: { children: React.ReactNode }) {
  return (
    <Row className="modal-buttons">
      <Col flex="auto"></Col>
      <Col>{children}</Col>
    </Row>
  )
}

function ModalButton({
  label,
  primary,
  disabled,
  onClick
}: {
  label: string
  primary?: boolean
  disabled?: boolean
  onClick: () => void
}) {
  const type = primary ? 'primary' : 'default'
  const htmlType = primary ? 'submit' : undefined
  const style = primary ? undefined : { marginRight: '10px' }
  return (
    <Button
      type={type}
      htmlType={htmlType}
      disabled={disabled}
      aria-label={label}
      style={style}
      onClick={onClick}
    >
      {label}
    </Button>
  )
}
